import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, List, ListItemButton, ListItemText, Tooltip, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer } from '@mui/material';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import { asyncPoolAll, loadPaginatedFrontend, useFeathers } from '../../app/util';
import { ArrowRightAlt, CancelOutlined } from '@mui/icons-material';
import { History } from '@mui/icons-material';
import { TileSelectOption } from '../lower-order'
import { ShapefileUpload } from '../ShapefileUpload';
import { ArrowLeft } from '@mui/icons-material';
import { Shapefile } from '../../app/feathers/districts/Shapefile';
import { District } from '../../app/feathers/districts/District';
import { CreateDistrictDraft, ShapefileDataMatcher } from '../ShapefileDataMatcher';
import { InteractiveMap } from '../InteractiveMap';
import { formatGeoJSON, determineShapeFromCoordinates } from '../../app/util/geo-projection-helpers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { Application } from '@feathersjs/feathers';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import { GeoDistrictCreationError, GeoDistrictCreationResult, ShapefileUploadJob } from '../../app/feathers/districts/ShapefileUploadJob';
import CompositeIcon from './combine.png'
import UploadShpIcon from './upload-shp.png'
import PdfConversionIcon from './convert-pdf.png'
import { ShapefileComposer } from '../ShapefileComposer';

interface GeodistrictCreationModalProps {
  state: string;
  parentId?: string;
  type: string;
  forceCreation?: boolean;
  onSuccess: () => void;
  onModalLayoutChange?: (layout: 'default' | 'wide') => void;
  onInProgressChange?: (inProgress: boolean) => void;
}

export const GeodistrictCreationModal = ({
  state,
  parentId,
  type,
  forceCreation = false,
  onSuccess,
  onModalLayoutChange,
  onInProgressChange
}: GeodistrictCreationModalProps) => {
  const POLL_SELECT_FIELDS = [
    '_id',
    'status',
    'error',
    'totalRows',
    'loadedRows',
    'percent',
    'geoDistrictCreationStatus',
    'geoDistrictCreationPercent',
    'geoDistrictCreationProcessed',
    'geoDistrictCreationTotal',
    'geoDistrictCreationSucceeded',
    'geoDistrictCreationFailed',
    'geoDistrictCreationAcknowledged',
    'districtCreationStatus',
    'districtCreationPercent',
    'districtCreationTotal',
    'districtCreationProcessed',
    'districtCreationSucceeded',
    'districtCreationFailed',
    'source',
    'expectedExpiration',
    'votingType'
  ];
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [creationMode, setCreationMode] = useState<null | 'upload-shp' | 'composite' | 'pdf-conversion'>(null);
  /*
    Step 0: Select creation mode

    For 'upload-shp'
    step 1: upload shapefile and metadata
    step 2: match columns
    step 3: visualize and save

    for 'composite'
    Step 1: pair districts with building blocks
    Step 2: visualize and save
  */
  const [ currentStep, setCurrentStep ] = useState<0 | 1 | 2 | 3>(0);
  const [ error, setError ] = useState<string | null>(null);

  // State for composition mode
  const [composition, setComposition] = useState<Record<string, District[]>>({});

  // 'upload-shp'
  // for uploading shapefile...
  const [shapefiles, setShapefiles] = useState<Shapefile[]>();
  const [districtToShapefileIndex, setDistrictToShapefileIndex] = useState<Record<string, number>>({});
  const [districtToPopulation, setDistrictToPopulation] = useState<Record<string, number>>({});
  const [createDistrictDraft, setCreateDistrictDraft] = useState<CreateDistrictDraft | null>(null);
  const [stateConfig, setStateConfig] = useState<StateConfiguration | null>(null);
  const [districtTypeOptions, setDistrictTypeOptions] = useState<string[]>([]);
  const [selectedDistrictType, setSelectedDistrictType] = useState<string>('');
  const [savingShapefiles, setSavingShapefiles] = useState(false);
  const [savingComposite, setSavingComposite] = useState(false);
  const [compositeSaveComplete, setCompositeSaveComplete] = useState(false);
  const [saveProgress, setSaveProgress] = useState<{ total: number; completed: number }>({ total: 0, completed: 0 });
  const [activeShapefileUpload, setActiveShapefileUpload] = useState<ShapefileUploadJob | null>(null);
  const [loadingShapefileUpload, setLoadingShapefileUpload] = useState(false);
  const [showShapefileHistory, setShowShapefileHistory] = useState(false);
  const [loadingShapefileHistory, setLoadingShapefileHistory] = useState(false);
  const [shapefileHistory, setShapefileHistory] = useState<ShapefileUploadJob[]>([]);
  const [activeGeoDistrictCreationJobId, setActiveGeoDistrictCreationJobId] = useState<string | null>(null);
  const [handledGeoDistrictCreationJobId, setHandledGeoDistrictCreationJobId] = useState<string | null>(null);
  const [matchedGeometryByDataIndex, setMatchedGeometryByDataIndex] = useState<Record<number, unknown>>({});
  const [lastLoadedGeometryKey, setLastLoadedGeometryKey] = useState<string>('');
  const [districts, setDistricts] = useState<District[]>([]);
  const [parentDistrict, setParentDistrict] = useState<District | null>(null);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [districtsError, setDistrictsError] = useState<string | null>(null);

  const typeConfiguration = stateConfig?.typeConfigurations.find(typeConfig => typeConfig.type === type);
  const parentTypeConfiguration = parentDistrict
    ? stateConfig?.typeConfigurations.find(typeConfig => typeConfig.type === parentDistrict.type)
    : null;
  const isTier2Type = typeConfiguration?.tierNumber === 2;
  const isParentScopedChildType = Boolean(
    !isTier2Type
    && parentId
    && typeConfiguration?.tierNumber
    && parentTypeConfiguration?.tierNumber
    && typeConfiguration.tierNumber === parentTypeConfiguration.tierNumber + 1
  );
  const effectiveTier2Type = isTier2Type ? type : (parentDistrict?.type || districtTypeOptions[0] || '');
  const effectiveTier2DistrictId = parentId;
  const effectiveStateDistrictId = isTier2Type ? parentId : parentDistrict?.parentId;
  const matcherDistrictTypeOptions = isParentScopedChildType ? [type] : districtTypeOptions;
  const previewCreateRows = useMemo(() => (
    createDistrictDraft?.enabled && Object.keys(districtToShapefileIndex).length === 0
      ? createDistrictDraft.rows
      : []
  ), [createDistrictDraft, districtToShapefileIndex]);

  const mapShapefileRows = (rows: Shapefile[] = []) => rows.map((row, idx) => {
    const nextRow = { ...row };
    if(typeof nextRow.dataIndex !== 'number') {
      nextRow.dataIndex = idx;
    }
    return nextRow;
  });

  const hasGeoCreationStarted = (job: ShapefileUploadJob | null) => (
    Boolean(job?.geoDistrictCreationStatus) && job?.geoDistrictCreationStatus !== 'idle'
  );

  const hasShapefileMetadata = (job: ShapefileUploadJob | null) => (
    Boolean(job?.source && job?.expectedExpiration)
  );

  const hydrateGeoCreationMappings = (job: ShapefileUploadJob | null) => {
    const payloadPairs = job?.geoDistrictCreationPayload?.districtToShapefileIndex;
    const payloadPopulation = job?.geoDistrictCreationPayload?.districtToPopulation;
    if(payloadPairs && typeof payloadPairs === 'object') {
      setDistrictToShapefileIndex(payloadPairs);
      if(payloadPopulation && typeof payloadPopulation === 'object') {
        setDistrictToPopulation(payloadPopulation);
      }
      return;
    }

    const successEntries: GeoDistrictCreationResult[] = Array.isArray(job?.geoDistrictCreationDistricts)
      ? (job?.geoDistrictCreationDistricts ?? [])
      : [];
    const pairedFromSuccess = successEntries
      .reduce((acc: Record<string, number>, entry: GeoDistrictCreationResult) => {
        if(entry?.districtId && typeof entry?.dataIndex === 'number') {
          acc[entry.districtId] = entry.dataIndex;
        }
        return acc;
      }, {});
    const errorEntries: GeoDistrictCreationError[] = Array.isArray(job?.geoDistrictCreationErrors)
      ? (job?.geoDistrictCreationErrors ?? [])
      : [];
    const pairedFromErrors = errorEntries
      .reduce((acc: Record<string, number>, entry: GeoDistrictCreationError) => {
        if(entry?.districtId && typeof entry?.dataIndex === 'number') {
          acc[entry.districtId] = entry.dataIndex;
        }
        return acc;
      }, {});
    const restoredPairs = { ...pairedFromSuccess, ...pairedFromErrors };
    if(Object.keys(restoredPairs).length > 0) {
      setDistrictToShapefileIndex(restoredPairs);
    }
  };

  const deleteShapefileRow = async (dataIndex: number) => {
    if(!feathers || !activeShapefileUpload?._id) {
      throw new Error('No active shapefile upload job found.');
    }

    setError(null);
    const updatedJob = await feathers.getService('shapefiles').patch(activeShapefileUpload._id, {
      $deleteRow: { dataIndex }
    });
    const pairedDistrictIds = Object.entries(districtToShapefileIndex)
      .filter(([, pairedDataIndex]) => pairedDataIndex === dataIndex)
      .map(([districtId]) => districtId);

    setShapefiles((current) => current?.filter((row) => row.dataIndex !== dataIndex));
    setDistrictToShapefileIndex((current) => Object.fromEntries(
      Object.entries(current).filter(([, pairedDataIndex]) => pairedDataIndex !== dataIndex)
    ));
    setDistrictToPopulation((current) => Object.fromEntries(
      Object.entries(current).filter(([districtId]) => !pairedDistrictIds.includes(districtId))
    ));
    setMatchedGeometryByDataIndex((current) => Object.fromEntries(
      Object.entries(current).filter(([rowDataIndex]) => Number(rowDataIndex) !== dataIndex)
    ));
    setLastLoadedGeometryKey('');
    setActiveShapefileUpload((current) => current ? {
      ...current,
      ...updatedJob,
      rows: current.rows?.filter((row) => row.dataIndex !== dataIndex)
    } : updatedJob);
  };

  useEffect(() => {
    // every time current step resets to 0, reset the data
    // reset data
    if(currentStep === 0) {
      setCreationMode(null);
      setShapefiles(undefined)
      setDistrictToShapefileIndex({});
      setDistrictToPopulation({});
      setCreateDistrictDraft(null);
      setSavingShapefiles(false);
      setSavingComposite(false);
      setCompositeSaveComplete(false);
      setSaveProgress({ total: 0, completed: 0 });
      setComposition({});
      setActiveShapefileUpload(null);
      setLoadingShapefileUpload(false);
      setShowShapefileHistory(false);
      setLoadingShapefileHistory(false);
      setShapefileHistory([]);
      setActiveGeoDistrictCreationJobId(null);
      setHandledGeoDistrictCreationJobId(null);
      setMatchedGeometryByDataIndex({});
      setLastLoadedGeometryKey('');
    }
  }, [ currentStep ]);

  useEffect(() => {
    const shouldUseWideLayout = (
      (creationMode === 'upload-shp' && currentStep === 2 && Boolean(shapefiles?.length))
      || (creationMode === 'composite' && currentStep === 1)
    );
    onModalLayoutChange?.(shouldUseWideLayout ? 'wide' : 'default');
  }, [creationMode, currentStep, shapefiles, onModalLayoutChange]);

  useEffect(() => {
    const hasTerminalGeoCreationStatus = ['complete', 'partial', 'error', 'canceled'].includes(activeShapefileUpload?.geoDistrictCreationStatus || '');
    const hasUnfinishedCompositeWork = creationMode === 'composite' && !compositeSaveComplete && (
      currentStep > 0
      || Object.keys(composition).length > 0
      || savingComposite
    );
    const hasUnfinishedShapefileWork = creationMode === 'upload-shp' && !hasTerminalGeoCreationStatus && (
      currentStep > 0
      || Boolean(shapefiles?.length)
      || Object.keys(districtToShapefileIndex).length > 0
      || activeShapefileUpload?.status === 'in-progress'
      || activeShapefileUpload?.geoDistrictCreationStatus === 'in-progress'
      || savingShapefiles
    );
    const inProgress = (
      hasUnfinishedCompositeWork
      || hasUnfinishedShapefileWork
    );
    onInProgressChange?.(inProgress);
  }, [creationMode, currentStep, shapefiles, districtToShapefileIndex, composition, activeShapefileUpload?.status, activeShapefileUpload?.geoDistrictCreationStatus, savingShapefiles, savingComposite, compositeSaveComplete, onInProgressChange]);

  useEffect(() => {
    if (!feathers || !parentId || !type) {
      setDistricts([]);
      setLoadingDistricts(false);
      setDistrictsError(null);
      return;
    }

    let cancelled = false;
    const loadDistricts = async () => {
      setLoadingDistricts(true);
      setDistrictsError(null);
      try {
        const loadedDistricts = await loadPaginatedFrontend<District>(
          'districts',
          {
            parentId,
            type,
            $sort: { name: 1 },
          },
          feathers,
          50,
        );
        if (!cancelled) {
          setDistricts(loadedDistricts || []);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setDistricts([]);
          setDistrictsError('Failed to load districts.');
        }
      } finally {
        if (!cancelled) {
          setLoadingDistricts(false);
        }
      }
    };

    loadDistricts();
    return () => {
      cancelled = true;
    };
  }, [feathers, parentId, type]);

  useEffect(() => {
    if (!feathers || creationMode !== 'upload-shp' || currentStep !== 3) {
      return;
    }

    const loadedDistrictIds = new Set(districts.map(district => `${district._id}`));
    const missingDistrictIds = Object.keys(districtToShapefileIndex)
      .filter(districtId => !loadedDistrictIds.has(`${districtId}`));
    if(missingDistrictIds.length === 0) {
      return;
    }
    const districtIdChunks = Array.from(
      { length: Math.ceil(missingDistrictIds.length / 25) },
      (_, index) => missingDistrictIds.slice(index * 25, (index + 1) * 25),
    );

    let cancelled = false;
    const loadRestoredJobDistricts = async () => {
      try {
        const restoredDistricts = (await asyncPoolAll(5, districtIdChunks, async (districtIds: string[]) => {
          const result = await feathers.getService('districts').find({
            query: {
              _id: districtIds,
              $limit: districtIds.length,
            },
          });
          return result.data || [];
        })).flat();
        if(!cancelled && restoredDistricts?.length) {
          setDistricts(currentDistricts => {
            const currentIds = new Set(currentDistricts.map(district => `${district._id}`));
            return [
              ...currentDistricts,
              ...restoredDistricts.filter(district => !currentIds.has(`${district._id}`)),
            ];
          });
        }
      } catch (e) {
        console.error(e);
      }
    };

    loadRestoredJobDistricts();
    return () => {
      cancelled = true;
    };
  }, [feathers, creationMode, currentStep, districtToShapefileIndex, districts]);

  useEffect(() => {
    const loadDistrictTypeOptions = async () => {
      if (!feathers || !state) {
        setStateConfig(null);
        setParentDistrict(null);
        setDistrictTypeOptions([]);
        setSelectedDistrictType('');
        return;
      }

      try {
        const [
          stateConfig,
          parentDistrict,
        ] = await Promise.all([
          feathers.getService('state-configurations').get(state) as Promise<StateConfiguration>,
          parentId ? feathers.getService('districts').get(parentId) as Promise<District> : Promise.resolve(null),
        ]);
        setStateConfig(stateConfig);
        setParentDistrict(parentDistrict);
        const tier2Types = stateConfig.typeConfigurations
          .filter(typeConfig => typeConfig.tierNumber === 2)
          .map(typeConfig => typeConfig.type);
        const fallbackTier2Type = tier2Types[0];

        if (!fallbackTier2Type) {
          setDistrictTypeOptions([]);
          setSelectedDistrictType('');
          return;
        }

        const typeConfiguration = stateConfig.typeConfigurations.find(typeConfig => typeConfig.type === type);
        const parentTypeConfiguration = parentDistrict
          ? stateConfig.typeConfigurations.find(typeConfig => typeConfig.type === parentDistrict.type)
          : null;
        const selectedTier2Type = typeConfiguration?.tierNumber === 2
          ? type
          : parentTypeConfiguration?.tierNumber === 2
            ? parentDistrict?.type || fallbackTier2Type
            : fallbackTier2Type;

        const visited = new Set<string>();
        const collectDescendants = (districtType: string): string[] => {
          const children = stateConfig.typeStructure?.[districtType] || [];
          return children.flatMap((childType) => {
            if (visited.has(childType)) {
              return [];
            }
            visited.add(childType);
            return [childType, ...collectDescendants(childType)];
          });
        };

        visited.add(selectedTier2Type);
        const options = [selectedTier2Type, ...collectDescendants(selectedTier2Type)];
        setDistrictTypeOptions(options);
        setSelectedDistrictType(options.includes(type) ? type : selectedTier2Type);
      } catch (e) {
        console.error(e);
        setStateConfig(null);
        setParentDistrict(null);
        setDistrictTypeOptions([]);
        setSelectedDistrictType('');
      }
    };

    loadDistrictTypeOptions();
  }, [feathers, state, parentId, type]);

  useEffect(() => {
    const loadActiveShapefileUpload = async () => {
      if(
        !feathers
        || creationMode !== 'upload-shp'
        || currentStep !== 1
        || !state
        || !effectiveTier2Type
      ) {
        return;
      }
      setLoadingShapefileUpload(true);
      try {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        const query: Record<string, unknown> = {
          stateCode: state,
          tier2Type: effectiveTier2Type,
          createdAt: { $gte: oneWeekAgo.toISOString() },
          status: { $in: ['processed', 'used', 'in-progress'] },
          geoDistrictCreationStatus: { $ne: 'canceled' },
          geoDistrictCreationAcknowledged: { $ne: true },
          $limit: 1,
          $sort: { createdAt: -1 }
        };
        if(effectiveTier2DistrictId) {
          query.tier2DistrictId = effectiveTier2DistrictId;
        }
        const result = await feathers.getService('shapefiles').find({ query });
        let latestUpload = result?.data?.[0] || null;
        if(latestUpload?._id) {
          latestUpload = await feathers.getService('shapefiles').get(latestUpload._id, {
            query: {
              $includeGeometry: true
            }
          });
        }
        setActiveShapefileUpload(latestUpload);
        if(latestUpload?.rows?.length) {
          setShapefiles(mapShapefileRows(latestUpload.rows || []));
        }
        if(hasGeoCreationStarted(latestUpload)) {
          hydrateGeoCreationMappings(latestUpload);
          setCurrentStep(3);
        } else if(hasShapefileMetadata(latestUpload)) {
          setCurrentStep(2);
        }
      } catch (e) {
        console.error(e);
        setActiveShapefileUpload(null);
      } finally {
        setLoadingShapefileUpload(false);
      }
    };
    loadActiveShapefileUpload();
  }, [feathers, creationMode, currentStep, state, effectiveTier2Type, effectiveTier2DistrictId]);

  const loadShapefileHistory = async () => {
    if(!feathers || !state || !effectiveTier2Type) {
      return;
    }
    setLoadingShapefileHistory(true);
    try {
      const query: Record<string, unknown> = {
        stateCode: state,
        tier2Type: effectiveTier2Type,
        $limit: 20,
        $sort: { createdAt: -1 },
        $includeInactive: true
      };
      if(effectiveTier2DistrictId) {
        query.tier2DistrictId = effectiveTier2DistrictId;
      }
      const result = await feathers.getService('shapefiles').find({ query });
      setShapefileHistory(result?.data || []);
    } catch (e) {
      console.error(e);
      setShapefileHistory([]);
    } finally {
      setLoadingShapefileHistory(false);
    }
  };

  const selectShapefileJob = async (jobId: string) => {
    if(!feathers || !jobId) {
      return;
    }
    try {
      const selectedJobWithGeometry = await feathers.getService('shapefiles').get(jobId, {
        query: {
          $includeGeometry: true
        }
      });
      setActiveShapefileUpload(selectedJobWithGeometry);
      if(selectedJobWithGeometry?.rows?.length) {
        setShapefiles(mapShapefileRows(selectedJobWithGeometry.rows));
      }
      if(hasGeoCreationStarted(selectedJobWithGeometry)) {
        hydrateGeoCreationMappings(selectedJobWithGeometry);
        setCurrentStep(3);
      } else if(hasShapefileMetadata(selectedJobWithGeometry)) {
        setCurrentStep(2);
      }
      setShowShapefileHistory(false);
    } catch (e) {
      console.error(e);
      setError('Failed to load shapefile job.');
    }
  };

  useEffect(() => {
    if(
      !feathers
      || creationMode !== 'upload-shp'
      || currentStep !== 2
      || !activeShapefileUpload?._id
      || activeShapefileUpload.status !== 'in-progress'
    ) {
      return;
    }

    const pollInterval = window.setInterval(async () => {
      try {
        const latestJob = await feathers.getService('shapefiles').get(activeShapefileUpload._id, {
          query: {
            $includeRows: false,
            $select: POLL_SELECT_FIELDS
          }
        });

        if(['processed', 'used'].includes(latestJob?.status || '')) {
          const latestJobWithGeometry = await feathers.getService('shapefiles').get(activeShapefileUpload._id, {
            query: {
              $includeGeometry: true
            }
          });
          setActiveShapefileUpload(latestJobWithGeometry);
          setShapefiles(mapShapefileRows(latestJobWithGeometry.rows || []));
          return;
        }

        setActiveShapefileUpload(latestJob);
      } catch (e) {
        console.error(e);
      }
    }, 2000);

    return () => {
      window.clearInterval(pollInterval);
    };
  }, [feathers, creationMode, currentStep, activeShapefileUpload?._id, activeShapefileUpload?.status]);

  useEffect(() => {
    if(!feathers || !activeShapefileUpload?._id || activeShapefileUpload?.geoDistrictCreationStatus !== 'in-progress') {
      return;
    }
    const pollInterval = window.setInterval(async () => {
      try {
        const latestJobWithGeometry = await feathers.getService('shapefiles').get(activeShapefileUpload._id, {
          query: {
            $includeRows: false
          }
        });
        setActiveShapefileUpload(latestJobWithGeometry);
      } catch (e) {
        console.error(e);
      }
    }, 2000);
    return () => window.clearInterval(pollInterval);
  }, [feathers, activeShapefileUpload?._id, activeShapefileUpload?.geoDistrictCreationStatus]);

  useEffect(() => {
    const handleTerminalGeoCreation = async () => {
      if(!feathers || !activeShapefileUpload?._id || activeShapefileUpload?.geoDistrictCreationStatus === 'in-progress') {
        return;
      }
      if(activeShapefileUpload._id !== activeGeoDistrictCreationJobId) {
        return;
      }
      if(activeShapefileUpload._id === handledGeoDistrictCreationJobId) {
        return;
      }

      setSavingShapefiles(false);
      setHandledGeoDistrictCreationJobId(activeShapefileUpload._id);

      let finalizedJob = activeShapefileUpload;
      try {
        finalizedJob = await feathers.getService('shapefiles').get(activeShapefileUpload._id, {
          query: {
            $includeRows: false
          }
        });
        setActiveShapefileUpload(finalizedJob);
      } catch (e) {
        console.error(e);
      }

      if(finalizedJob?.geoDistrictCreationStatus === 'complete') {
        return;
      }
      if(finalizedJob?.geoDistrictCreationStatus === 'partial') {
        setError('Completed with some district errors. Review the failures below.');
        return;
      }
      if(finalizedJob?.geoDistrictCreationStatus === 'error') {
        const errorMessage = finalizedJob?.error?.message || 'Failed to create geo-districts.';
        setError(errorMessage);
      }
    };

    handleTerminalGeoCreation();
  }, [feathers, activeShapefileUpload, activeGeoDistrictCreationJobId, handledGeoDistrictCreationJobId, onSuccess]);

  useEffect(() => {
    const loadMatchedGeometries = async () => {
      if(
        !feathers
        || creationMode !== 'upload-shp'
        || currentStep !== 2
        || !activeShapefileUpload?._id
      ) {
        return;
      }
      const mappedIndexes = Object.values(districtToShapefileIndex);
      const previewIndexes = previewCreateRows.map((row) => row.dataIndex);
      const rowIndexes = [...new Set([...mappedIndexes, ...previewIndexes])]
        .filter((value): value is number => typeof value === 'number')
        .sort((a, b) => a - b);
      if(rowIndexes.length === 0) {
        return;
      }
      const geometryKey = `${activeShapefileUpload._id}:${rowIndexes.join(',')}`;
      if(geometryKey === lastLoadedGeometryKey) {
        return;
      }

      try {
        const jobWithGeometry = await feathers.getService('shapefiles').get(activeShapefileUpload._id, {
          query: {
            $includeRows: true,
            $includeGeometry: true,
            $rowIndexes: rowIndexes
          }
        });
        const nextGeometryByDataIndex = (jobWithGeometry?.rows || []).reduce((acc: Record<number, unknown>, row: Shapefile) => {
          if(typeof row?.dataIndex === 'number' && typeof row?.geometry !== 'undefined') {
            acc[row.dataIndex] = row.geometry;
          }
          return acc;
        }, {});
        setMatchedGeometryByDataIndex((prev) => ({ ...prev, ...nextGeometryByDataIndex }));
        setLastLoadedGeometryKey(geometryKey);
      } catch (e) {
        console.error(e);
      }
    };

    loadMatchedGeometries();
  }, [feathers, creationMode, currentStep, activeShapefileUpload?._id, districtToShapefileIndex, previewCreateRows, lastLoadedGeometryKey]);

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const runWithRetry = async <T,>(task: () => Promise<T>) => {
    try {
      return await task();
    } catch (firstError) {
      await wait(400);
      return await task();
    }
  };

  const buildCreateDistrictsPayloadFromDraft = () => {
    if (!createDistrictDraft?.enabled || !selectedDistrictType) {
      return null;
    }
    const districtCreationParentId = isParentScopedChildType ? parentId : effectiveStateDistrictId;
    if (!districtCreationParentId) {
      throw new Error('Missing state context for district creation.');
    }
    if (!effectiveTier2Type) {
      throw new Error('Missing tier-2 type context for district creation.');
    }
    return {
      districtType: selectedDistrictType,
      stateDistrictId: districtCreationParentId,
      tier2Type: effectiveTier2Type,
      isSubtype: createDistrictDraft.isSubtype,
      ...(forceCreation && parentId ? { parentDistrictId: parentId } : {}),
      ...(activeShapefileUpload?.votingType ? { votingType: activeShapefileUpload.votingType } : {}),
      rows: createDistrictDraft.rows.map((row) => ({
        dataIndex: row.dataIndex,
        primaryValue: row.primaryValue,
        parentValue: row.parentValue,
      })),
    };
  };

  const saveShapefiles = async () => {
    setSavingShapefiles(true);
    setError(null)
    try {
      if (createDistrictDraft && !createDistrictDraft.enabled) {
        throw new Error(createDistrictDraft.validationError || 'Primary column setup is incomplete.');
      }
      const pairsToSave = districtToShapefileIndex;
      const createDistrictsFromRows = Object.keys(pairsToSave).length === 0
        ? buildCreateDistrictsPayloadFromDraft()
        : null;

      const shapefilePairs = Object.entries(pairsToSave);
      if(shapefilePairs.length === 0 && !createDistrictsFromRows) {
        throw new Error('No district matches found.');
      }
      if(!activeShapefileUpload?._id) {
        throw new Error('No active shapefile upload job found.');
      }

      const createdJob = await feathers.getService('shapefiles').patch(activeShapefileUpload._id, {
        $createGeoDistricts: {
          districtToShapefileIndex: Object.keys(pairsToSave).length > 0 ? pairsToSave : undefined,
          districtToPopulation: Object.keys(pairsToSave).length > 0 ? districtToPopulation : undefined,
          createDistrictsFromRows: createDistrictsFromRows || undefined,
        }
      });
      setActiveShapefileUpload(createdJob);
      setActiveGeoDistrictCreationJobId(createdJob?._id || activeShapefileUpload._id);
    } catch (e) {
      console.error(e);
      setSavingShapefiles(false);
      if(e instanceof Error) {
        setError(e.message);
      } else {
        setError('An unknown error occurred');
      }
    }
  }

  const saveComposite = async () => {
    setSavingComposite(true);
    setError(null);
    try {
      const compositionEntries = Object.entries(composition);
      setSaveProgress({ total: compositionEntries.length, completed: 0 });

      if(compositionEntries.length > 0) {
        await asyncPoolAll(5, compositionEntries, async ([targetDistrictId, buildingBlocks]: [string, District[]]) => {
          await runWithRetry(() => feathers.getService('geo-districts').create({
            district: targetDistrictId,
            $combineDistricts: buildingBlocks
          }));
          setSaveProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
          // Avoid retaining large created geodistrict payloads in local arrays.
          return undefined;
        });
      }
      
      setCompositeSaveComplete(true);
    } catch (e) {
      console.error(e);
      if(e instanceof Error) {
        setError(e.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setSavingComposite(false);
    }
  }

  const cancelGeoDistrictCreation = async () => {
    if(!feathers || !activeShapefileUpload?._id) {
      return;
    }
    try {
      const canceledJob = await feathers.getService('shapefiles').patch(activeShapefileUpload._id, { $cancel: true });
      setActiveShapefileUpload(canceledJob);
      setSavingShapefiles(false);
      setActiveGeoDistrictCreationJobId(null);
      setHandledGeoDistrictCreationJobId(null);
      setShapefiles(undefined);
      setDistrictToShapefileIndex({});
      setDistrictToPopulation({});
      setCreateDistrictDraft(null);
      setMatchedGeometryByDataIndex({});
      setLastLoadedGeometryKey('');
      setCurrentStep(1);
    } catch (e) {
      console.error(e);
      if(e instanceof Error) {
        setError(e.message);
      } else {
        setError('Failed to cancel geodistrict creation.');
      }
    }
  };

  const finishGeoDistrictCreation = async () => {
    if(
      feathers
      && activeShapefileUpload?._id
      && ['complete', 'partial', 'error'].includes(activeShapefileUpload?.geoDistrictCreationStatus || '')
    ) {
      try {
        await feathers.getService('shapefiles').patch(activeShapefileUpload._id, {
          geoDistrictCreationAcknowledged: true
        });
      } catch (e) {
        console.error(e);
        if(e instanceof Error) {
          setError(e.message);
        } else {
          setError('Failed to acknowledge geodistrict creation.');
        }
        return;
      }
    }

    onSuccess();
  };

  const buildingBlockMissingGeoboundary = (district: District) => (
    district.hasGeodistrict === false || !district.geoDistrict?.boundary
  );

  const advanceFromCompositeSelection = () => {
    const invalidBuildingBlocks = Object.values(composition)
      .flat()
      .filter(buildingBlockMissingGeoboundary);
    const uniqueInvalidNames = [...new Set(invalidBuildingBlocks.map((district) => district.longName || district.name || district._id))];

    if (uniqueInvalidNames.length > 0) {
      setError(`Cannot continue because selected building blocks are missing geoboundaries: ${uniqueInvalidNames.join(', ')}.`);
      return;
    }

    setError(null);
    setCurrentStep(2);
  };

  const geoCreationTotal = activeShapefileUpload?.geoDistrictCreationTotal || 0;
  const geoCreationProcessed = activeShapefileUpload?.geoDistrictCreationProcessed || 0;
  const geoCreationPercent = activeShapefileUpload?.geoDistrictCreationPercent || 0;
  const districtCreationTotal = activeShapefileUpload?.districtCreationTotal || 0;
  const districtCreationProcessed = activeShapefileUpload?.districtCreationProcessed || 0;
  const districtCreationPercent = activeShapefileUpload?.districtCreationPercent || 0;
  const districtCreationStatus = activeShapefileUpload?.districtCreationStatus || 'idle';
  const hasDistrictCreationPhase = districtCreationTotal > 0;
  const districtCreationInProgress = districtCreationStatus === 'in-progress';
  const districtCreationCompleteForUi = ['complete', 'partial', 'error', 'canceled'].includes(districtCreationStatus);
  const geoCreationErrorsRaw = activeShapefileUpload?.geoDistrictCreationErrors;
  const geoCreationErrors: GeoDistrictCreationError[] = Array.isArray(geoCreationErrorsRaw)
    ? geoCreationErrorsRaw
    : [];
  const districtNameById = districts.reduce((acc, district) => {
    acc[district._id] = district.longName || district._id;
    return acc;
  }, {} as Record<string, string>);
  const canShowMatcher = (
    Boolean(shapefiles?.length)
    && ['processed', 'used'].includes(activeShapefileUpload?.status || '')
  );
  const hasGeoCreationStartedForUi = ['in-progress', 'complete', 'partial', 'error', 'canceled'].includes(activeShapefileUpload?.geoDistrictCreationStatus || '');
  const isGeoCreationTerminal = ['complete', 'partial', 'error', 'canceled'].includes(activeShapefileUpload?.geoDistrictCreationStatus || '');
  const hideSaveButtonForGeoCreation = ['in-progress', 'complete', 'partial', 'error'].includes(activeShapefileUpload?.geoDistrictCreationStatus || '');
  const canCancelGeoDistrictCreation = ['idle', 'in-progress'].includes(activeShapefileUpload?.geoDistrictCreationStatus || '');
  const showDistrictCreationProgress = hasDistrictCreationPhase && (
    savingShapefiles
    || districtCreationInProgress
    || (!districtCreationCompleteForUi && hasGeoCreationStartedForUi)
  );
  const showGeoCreationProgress = (
    (savingShapefiles || ['idle', 'in-progress'].includes(activeShapefileUpload?.geoDistrictCreationStatus || ''))
    && geoCreationTotal > 0
    && (!hasDistrictCreationPhase || districtCreationCompleteForUi)
  );
  return (
    <div>
      {
        error && <Alert severity='error' style={{ marginBottom: 12 }}>{error}</Alert>
      }
      {
        districtsError && <Typography variant='body1' color='error'>{districtsError}</Typography>
      }
      {
        currentStep === 0 && <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <Typography variant='body1'>
            How do you want to create the boundaries?
          </Typography>
          <div style={{
            display: 'grid',
            gridGap: 16,
            alignSelf: 'center',
            gridTemplateColumns: 'repeat(3, 220px)'
          }}>
            <TileSelectOption
              title="Upload shapefile"
              subText="Upload a .shp file with the information"
              onChange={() => setCreationMode(cm => cm === 'upload-shp' ? null : 'upload-shp')}
              selected={creationMode === 'upload-shp'}
              value='upload-shp'
              style={{ maxWidth: 200 }}
            >
              <TileSelectInner>
                <img src={UploadShpIcon}/>
              </TileSelectInner>
            </TileSelectOption>
            <TileSelectOption
              title="Build from other districts"
              subText="Set up these district boundaries, using other district boundaries as building blocks"
              onChange={() => setCreationMode(cm => cm === 'composite' ? null : 'composite')}
              selected={creationMode === 'composite'}
              value='composite'
              style={{ maxWidth: 200 }}
            >
              <TileSelectInner>
                <img src={CompositeIcon}/>
              </TileSelectInner>
            </TileSelectOption>
            <TileSelectOption
              title="Convert a PDF"
              subText="Convert a PDF or image file with a static map"
              onChange={() => setCreationMode(cm => cm === 'pdf-conversion' ? null : 'pdf-conversion')}
              selected={creationMode === 'pdf-conversion'}
              value='pdf-conversion'
              style={{ maxWidth: 200 }}
              disabled={true}
            >
              <TileSelectInner>
                <img src={PdfConversionIcon} style={{ width: '150px', minWidth: '120px' }} />
              </TileSelectInner>
            </TileSelectOption>
          </div>
          <Button
            variant="contained"
            color="primary"
            disabled={!creationMode}
            onClick={() => setCurrentStep(1)}
            endIcon={<ArrowRightAlt/>}
            style={{ alignSelf: 'end' }}
          >
            Next
          </Button>
        </div>
      }
      {
        creationMode === 'upload-shp' && currentStep === 1 && 
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title='View previous jobs'>
              <IconButton
                size='small'
                onClick={() => {
                  setShowShapefileHistory(true);
                  loadShapefileHistory();
                }}
              >
                <History />
              </IconButton>
            </Tooltip>
          </div>
          {
            loadingShapefileUpload &&
            <Typography variant='body2' style={{ margin: '12px 0 20px', width: '100%', maxWidth: '640px' }}>
              Looking for recent uploads...
            </Typography>
          }
          {
            !loadingShapefileUpload &&
            <ShapefileUpload
              style={{ width: '100%', maxWidth: '760px', alignSelf: 'stretch', margin: shapefiles ? '-42px 0 24px' : '8px 0 24px' }}
              uploadScope={{
                stateCode: state,
                tier2Type: effectiveTier2Type,
                tier2DistrictId: effectiveTier2DistrictId
              }}
              activeUploadJob={activeShapefileUpload}
              onUploadJobChange={setActiveShapefileUpload}
              onChangeShapefiles={(data: Shapefile[]) => setShapefiles(data?.length ? data : undefined)}
              onNext={(job: ShapefileUploadJob) => {
                setActiveShapefileUpload(job);
                setCurrentStep(2);
              }}
            />
          }
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <BackButton onClick={() => setCurrentStep(0)}/>
            <div />
          </div>
        </div>
      }
      {
        creationMode === 'upload-shp' && currentStep === 2 &&
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title='View previous jobs'>
              <IconButton
                size='small'
                onClick={() => {
                  setShowShapefileHistory(true);
                  loadShapefileHistory();
                }}
              >
                <History />
              </IconButton>
            </Tooltip>
          </div>
          <ShapefileUpload
            style={{ width: '100%', maxWidth: '760px', alignSelf: 'stretch', margin: '-42px 0 24px' }}
            uploadScope={{
              stateCode: state,
              tier2Type: effectiveTier2Type,
              tier2DistrictId: effectiveTier2DistrictId
            }}
            activeUploadJob={activeShapefileUpload}
            metadataReadOnly={true}
            onUploadJobChange={(job: ShapefileUploadJob | null) => {
              setActiveShapefileUpload(job);
              if(!job) {
                setShapefiles(undefined);
                setDistrictToShapefileIndex({});
                setDistrictToPopulation({});
                setCreateDistrictDraft(null);
                setMatchedGeometryByDataIndex({});
                setLastLoadedGeometryKey('');
                setCurrentStep(1);
              }
            }}
            onChangeShapefiles={(data: Shapefile[]) => setShapefiles(data?.length ? data : undefined)}
            onNext={(job: ShapefileUploadJob) => {
              setActiveShapefileUpload(job);
            }}
          />
          {
            loadingDistricts &&
            <Typography variant='body2' style={{ margin: '0 0 16px' }}>
              Loading districts...
            </Typography>
          }
          {
            !canShowMatcher && activeShapefileUpload?.status !== 'in-progress' && !loadingDistricts &&
            <Typography variant='body2' style={{ margin: '0 0 16px' }}>
              Waiting for the shapefile to finish processing...
            </Typography>
          }
          {
            canShowMatcher && !loadingDistricts &&
            <ShapefileDataMatcher
              districts={districts}
              stateDistrictId={effectiveStateDistrictId || null}
              shapefiles={shapefiles || []}
              stateConfig={stateConfig}
              pairs={districtToShapefileIndex}
              districtTypeOptions={matcherDistrictTypeOptions}
              selectedDistrictType={selectedDistrictType}
              onChangeDistrictType={setSelectedDistrictType}
              forceCreation={forceCreation}
              onChangePairs={(newPairs) => setDistrictToShapefileIndex(newPairs)}
              onChangePopulation={(newPop) => setDistrictToPopulation(newPop)}
              onChangeCreateDraft={setCreateDistrictDraft}
              onDeleteShapefileRow={deleteShapefileRow}
            />
          }
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <BackButton onClick={() => setCurrentStep(1)}/>
            <NextButton
              onClick={() => setCurrentStep(3)}
              disabled={!canShowMatcher || (Object.entries(districtToShapefileIndex).length === 0 && !createDistrictDraft?.enabled)}
            />
          </div>
        </div>
      }
      {
        creationMode === 'composite' && currentStep === 1 &&
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: '100%' }}>
          <div style={{ maxHeight: 'calc(100vh - 270px)', overflowY: 'auto', marginBottom: 16, paddingRight: 4 }}>
            {
              loadingDistricts
                ? <Typography variant='body2' style={{ margin: '0 0 16px' }}>Loading districts...</Typography>
                : <ShapefileComposer
                    districts={districts}
                    onChangeComposition={(newComposition) => {
                      setError(null);
                      setComposition(newComposition);
                    }}
                  />
            }
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
            <BackButton onClick={() => setCurrentStep(0)}/>
            <NextButton 
              onClick={advanceFromCompositeSelection}
              disabled={Object.keys(composition).length === 0}
            />
          </div>
        </div>
      }
      {
        creationMode === 'upload-shp' && currentStep === 3 &&
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: '100%' }}>
          <div style={{ height: '300px', marginBottom: 10 }}>
            <InteractiveMap
              style={{}}
              districts={undefined}
              features={[
                ...Object.entries(districtToShapefileIndex).map(([districtId, dataIdx]) => {
                  const district = districts.find(d => d._id === districtId);
                  const shapefileRow = shapefiles?.find(d => d.dataIndex === dataIdx);
                  const boundary = matchedGeometryByDataIndex[dataIdx] ?? shapefileRow?.geometry;
                  
                  const populationForDistrict = districtToPopulation[districtId];
                  if(!boundary) return null;

                  const districtLabel = district?.longName || districtId;
                  const tooltip = populationForDistrict ? `${districtLabel} (Population: ${populationForDistrict.toLocaleString()})` : districtLabel;
                  const geoJSON = formatGeoJSON(boundary, { fillColor: '#4F50CC', strokeColor: '#4F50CC', tooltip });
                  return geoJSON;
                }),
                ...previewCreateRows.map((row) => {
                  const shapefileRow = shapefiles?.find(d => d.dataIndex === row.dataIndex);
                  const boundary = matchedGeometryByDataIndex[row.dataIndex] ?? shapefileRow?.geometry;
                  if(!boundary) return null;
                  return formatGeoJSON(boundary, {
                    fillColor: '#4F50CC',
                    strokeColor: '#4F50CC',
                    tooltip: `${row.primaryValue} (new district)`
                  });
                })
              ].filter(Boolean)}
            />
          </div>
          {
            showDistrictCreationProgress &&
            <div style={{ margin: '12px 0 8px' }}>
              <Typography variant='body2' style={{ marginBottom: 6 }}>
                Creating districts {districtCreationProcessed} of {districtCreationTotal}
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <LinearProgress
                  variant='determinate'
                  value={districtCreationPercent}
                  style={{ flexGrow: 1 }}
                />
                {
                  canCancelGeoDistrictCreation &&
                  <Tooltip title='Cancel job'>
                    <IconButton size='small' color='inherit' onClick={cancelGeoDistrictCreation}>
                      <CancelOutlined fontSize='small' />
                    </IconButton>
                  </Tooltip>
                }
              </div>
            </div>
          }
          {
            showGeoCreationProgress &&
            <div style={{ margin: '12px 0 8px' }}>
              <Typography variant='body2' style={{ marginBottom: 6 }}>
                Creating geodistricts {geoCreationProcessed} of {geoCreationTotal}
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <LinearProgress
                  variant='determinate'
                  value={geoCreationPercent}
                  style={{ flexGrow: 1 }}
                />
                {
                  canCancelGeoDistrictCreation &&
                  <Tooltip title='Cancel job'>
                    <IconButton size='small' color='inherit' onClick={cancelGeoDistrictCreation}>
                      <CancelOutlined fontSize='small' />
                    </IconButton>
                  </Tooltip>
                }
              </div>
            </div>
          }
          {
            isGeoCreationTerminal &&
            <div style={{ margin: '6px 0 8px' }}>
              <Typography variant='body2'>
                Job status: <b>{activeShapefileUpload?.geoDistrictCreationStatus}</b>
              </Typography>
              {
                hasDistrictCreationPhase &&
                <Typography variant='body2'>
                  District creation: {districtCreationProcessed} of {districtCreationTotal} processed; {activeShapefileUpload?.districtCreationSucceeded || 0} succeeded; {activeShapefileUpload?.districtCreationFailed || 0} failed.
                </Typography>
              }
              <Typography variant='body2'>
                Geodistrict creation: {geoCreationProcessed} of {geoCreationTotal} processed; {activeShapefileUpload?.geoDistrictCreationSucceeded || 0} succeeded; {activeShapefileUpload?.geoDistrictCreationFailed || 0} failed.
              </Typography>
            </div>
          }
          {
            geoCreationErrors.length > 0 &&
            <div style={{ margin: '8px 0 12px' }}>
              <Typography variant='body2' color='error' style={{ marginBottom: 6 }}>
                {geoCreationErrors.length} districts failed:
              </Typography>
              <TableContainer component={Paper} style={{ maxHeight: '200px' }}>
                <Table size='small' stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>District</TableCell>
                      <TableCell>Stage</TableCell>
                      <TableCell>Error</TableCell>
                      <TableCell>Attempts</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {geoCreationErrors.map((entry: GeoDistrictCreationError, idx: number) => {
                      const districtId = entry?.districtId;
                      const districtLabel = districtNameById[districtId] || districtId || 'Unknown district';
                      return (
                        <TableRow key={`${districtId || 'unknown'}-${idx}`}>
                          <TableCell>{districtLabel}</TableCell>
                          <TableCell>{entry?.stage || '-'}</TableCell>
                          <TableCell>{entry?.message || 'Unknown error'}</TableCell>
                          <TableCell>{entry?.attempts || 0}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          }
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {
              !hasGeoCreationStartedForUi
                ? <BackButton onClick={() => setCurrentStep(2)}/>
                : <div />
            }
            <div style={{ display: 'flex', gap: 12 }}>
              {
                activeShapefileUpload?.geoDistrictCreationStatus === 'in-progress' &&
                <Button variant='outlined' color='inherit' onClick={cancelGeoDistrictCreation}>
                  Cancel job
                </Button>
              }
              {
                !hideSaveButtonForGeoCreation &&
                <SaveButton onClick={() => saveShapefiles()} saving={savingShapefiles}/>
              }
              {
                isGeoCreationTerminal &&
                <Button variant='contained' color='primary' onClick={finishGeoDistrictCreation}>
                  Done
                </Button>
              }
            </div>
          </div>
        </div>
      }
      {
        creationMode === 'composite' && currentStep === 2 &&
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: '100%', height: '600px' }}>
          <InteractiveMap
            style={{}}
            districts={undefined}
            features={Object.entries(composition).map(([targetDistrictId, buildingBlocks]) => {
              const targetDistrict = districts.find(d => d._id === targetDistrictId);
              if (!targetDistrict || buildingBlocks.length === 0) return null;

              // Process all building blocks
              const allPolygons: number[][][] = [];
              
              for (const block of buildingBlocks) {
                if (!block.geoDistrict?.boundary) continue;
                
                const shapeType = determineShapeFromCoordinates(block.geoDistrict.boundary as number[][]);
                
                if (shapeType === 'Point' || shapeType === 'LineString') {
                  throw new Error(`Cannot combine ${shapeType} geometries. Only Polygon and MultiPolygon are supported.`);
                }
                
                if (shapeType === 'Polygon') {
                  allPolygons.push(block.geoDistrict.boundary as number[][]);
                } else if (shapeType === 'MultiPolygon') {
                  // Spread the MultiPolygon into individual polygons
                  allPolygons.push(...(block.geoDistrict.boundary as number[][][]));
                }
              }
              
              if (allPolygons.length === 0) return null;
              
              // Create a MultiPolygon from all the polygons
              return formatGeoJSON(allPolygons, {
                fillColor: '#4F50CC',
                strokeColor: '#4F50CC',
                tooltip: targetDistrict.longName
              });
            }).filter(Boolean)}
          />
          {
            savingComposite && saveProgress.total > 0 &&
            <div style={{ margin: '12px 0 8px' }}>
              <Typography variant='body2' style={{ marginBottom: 6 }}>
                Saving {saveProgress.completed} of {saveProgress.total}
              </Typography>
              <LinearProgress
                variant='determinate'
                value={(saveProgress.completed / saveProgress.total) * 100}
              />
            </div>
          }
          {
            compositeSaveComplete &&
            <div style={{ margin: '12px 0 8px' }}>
              <Typography variant='body2' color='primary'>
                Composite geodistricts created successfully.
              </Typography>
            </div>
          }
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {
              compositeSaveComplete
                ? <div />
                : <BackButton onClick={() => setCurrentStep(1)}/>
            }
            {
              compositeSaveComplete
                ? (
                  <Button variant='contained' color='primary' onClick={onSuccess}>
                    Done
                  </Button>
                )
                : <SaveButton onClick={saveComposite} saving={savingComposite}/>
            }
          </div>
        </div>
      }
      <Dialog
        open={showShapefileHistory}
        onClose={() => setShowShapefileHistory(false)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Previous shapefile jobs</DialogTitle>
        <DialogContent dividers>
          {
            loadingShapefileHistory &&
            <Typography variant='body2'>Loading jobs...</Typography>
          }
          {
            !loadingShapefileHistory && shapefileHistory.length === 0 &&
            <Typography variant='body2'>No previous jobs found.</Typography>
          }
          {
            !loadingShapefileHistory && shapefileHistory.length > 0 &&
            <List dense>
              {shapefileHistory.map((job) => (
                <ListItemButton key={job._id} onClick={() => selectShapefileJob(job._id)}>
                  <ListItemText
                    primary={`${job.status} - ${new Date(job.createdAt || 0).toLocaleString()}`}
                    secondary={
                      job?.geoDistrictCreationStatus
                        ? `Geodistrict creation: ${job.geoDistrictCreationStatus}`
                        : ''
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowShapefileHistory(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const BackButton = ({
  onClick,
}: {
  onClick: () => void;
}) => (
  <Button
    onClick={onClick}
    startIcon={<ArrowLeft/>}
  >
    Back
  </Button>
)

const NextButton = ({
  onClick,
  disabled
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <Button
    style={{
      alignSelf: 'end',
    }}
    variant="contained"
    color="primary"
    disabled={disabled}
    onClick={onClick}
    endIcon={<ArrowRightAlt/>}
  >
    Next
  </Button>
)

const SaveButton = ({
  onClick,
  disabled,
  saving
}: {
  onClick: () => void;
  disabled?: boolean;
  saving: boolean;
}) => (
  <Button
    style={{
      alignSelf: 'end',
    }}
    variant="contained"
    color="primary"
    disabled={disabled || saving}
    onClick={onClick}
  >
    {saving ? 'Saving...' : 'Save'}
  </Button>
)

const TileSelectInner = styled.div`
  width: 100%;
  height: 60px;
  padding-bottom: 12px;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100%;
    object-fit: contain;
  }
`;